// BARCINO — Catalogue / catégories
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const barcinoCatalogue = {
    _heroTween: null,
    _root: null,
    _button: null,
    _panel: null,
    _toggle_handler: null,
    _close_handler: null,
    _key_handler: null,
    _group_handler: null,
    _change_handler: null,
    _countTimer: null,
    _countSequence: 0,

    init() {
        this.destroy();

        this._initHeroParallax();

        this._root = document.querySelector('[data-catalogue]');
        if (!this._root) return;

        this._button = this._root.querySelector('[data-catalogue-filter-toggle]');
        this._panel = this._root.querySelector('[data-catalogue-filter]');
        if (!this._button || !this._panel) return;

        this._toggle_handler = () => this._setOpen(true);
        this._close_handler = () => this._setOpen(false);
        this._key_handler = (event) => {
            if (event.key === 'Escape') this._setOpen(false);
        };
        this._button.addEventListener('click', this._toggle_handler);
        this._panel.querySelectorAll('[data-catalogue-filter-close]').forEach(button => {
            button.addEventListener('click', this._close_handler);
        });
        document.addEventListener('keydown', this._key_handler);

        // Stoppe la propagation de TOUS les clics dans le panneau avant qu'ils
        // n'atteignent le listener global de Barba sur document — sur les pages
        // Barba (catalogue), Barba appelle preventDefault() sur tout clic qui ne
        // résout pas vers un <a href>, ce qui casse le cochage natif des
        // radios/checkboxes du formulaire de filtres (sans propagation stoppée
        // ici, aucune case ni bouton radio n'est cochable).
        this._group_handler = (event) => {
            event.stopPropagation();

            const summary = event.target.closest('summary');
            if (!summary || !this._panel.contains(summary)) return;
            const details = summary.parentElement;
            if (!details || details.tagName !== 'DETAILS') return;

            event.preventDefault();
            details.open = !details.open;
        };
        this._panel.addEventListener('click', this._group_handler);

        this._bindIntensitySlider();
        this._bindPriceRange();
        this._bindLiveCount();
        this._bindReset();
    },

    // Le lien "Réinitialiser" pointe vers l'URL catalogue sans filtre, mais les
    // filtres ne sont jamais reflétés dans l'URL (tout passe par l'AJAX live) :
    // Barba voit donc un lien vers la page déjà affichée et ne fait rien. On
    // réinitialise le formulaire nous-mêmes et on redéclenche le live count.
    _bindReset() {
        const resetLink = this._panel.querySelector('[data-catalogue-reset]');
        const form = this._panel.querySelector('form');
        if (!resetLink || !form) return;

        resetLink.addEventListener('click', (event) => {
            event.preventDefault();
            form.reset();
            form.querySelectorAll('[data-price-handle], [data-intensity-input]').forEach(input => {
                input.dispatchEvent(new Event('input', { bubbles: true }));
            });
            // L'écoute du curseur d'intensité s'auto-active un name dès le
            // premier "input" (voir _bindIntensitySlider) — la resynchro
            // visuelle ci-dessus le déclenche aussi, il faut donc le retirer
            // pour que le filtre reste réellement désactivé après reset.
            const intensityInput = form.querySelector('[data-intensity-input]');
            if (intensityInput) intensityInput.removeAttribute('name');
            this._updateResults();
        });
    },

    _setOpen(open) {
        if (!this._panel || !this._button) return;
        this._panel.classList.toggle('is-open', open);
        this._panel.setAttribute('aria-hidden', String(!open));
        this._panel.inert = !open;
        this._button.classList.toggle('is-active', open);
        document.body.style.overflow = open ? 'hidden' : '';
    },

    // ── Profil — curseur d'intensité à une poignée ─────────────────────────
    _bindIntensitySlider() {
        const wrap = this._panel.querySelector('[data-intensity-slider]');
        if (!wrap) return;

        const input = wrap.querySelector('[data-intensity-input]');
        const label = wrap.querySelector('[data-intensity-value]');
        if (!input) return;

        let names = [];
        try { names = JSON.parse(wrap.dataset.intensityNames || '[]'); } catch { /* ignore */ }

        const update = () => {
            const level = parseInt(input.value, 10);
            if (label) label.textContent = names[level - 1] || '';
        };
        update();
        input.addEventListener('input', () => {
            // N'envoie le filtre qu'à partir du premier déplacement réel du curseur
            // (voir commentaire dans _products.php) — sans ça le filtre "intensité"
            // s'applique silencieusement dès le chargement, avec la valeur par défaut.
            if (!input.name) input.name = 'intensity';
            update();
            this._scheduleCount();
        });
    },

    // ── Prix — double curseur synchronisé avec les champs numériques ───────
    _bindPriceRange() {
        const wrap = this._panel.querySelector('[data-price-range]');
        if (!wrap) return;

        const minHandle = wrap.querySelector('[data-price-handle="min"]');
        const maxHandle = wrap.querySelector('[data-price-handle="max"]');
        const minNumber = wrap.querySelector('[data-price-number="min"]');
        const maxNumber = wrap.querySelector('[data-price-number="max"]');
        const fill = wrap.querySelector('[data-price-fill]');
        if (!minHandle || !maxHandle || !minNumber || !maxNumber || !fill) return;

        const boundMin = parseFloat(wrap.dataset.priceMinBound || '0');
        const boundMax = parseFloat(wrap.dataset.priceMaxBound || '0');
        const span = (boundMax - boundMin) || 1;

        const updateFill = () => {
            const minVal = parseFloat(minHandle.value);
            const maxVal = parseFloat(maxHandle.value);
            const left = ((minVal - boundMin) / span) * 100;
            const right = ((maxVal - boundMin) / span) * 100;
            fill.style.left = `${Math.max(0, left)}%`;
            fill.style.width = `${Math.max(0, right - left)}%`;
        };

        const clamp = (value) => Math.max(boundMin, Math.min(value, boundMax));

        const syncFromHandles = () => {
            let minVal = parseFloat(minHandle.value);
            let maxVal = parseFloat(maxHandle.value);
            if (minVal > maxVal) { const tmp = minVal; minVal = maxVal; maxVal = tmp; }
            minHandle.value = String(minVal);
            maxHandle.value = String(maxVal);
            minNumber.value = String(minVal);
            maxNumber.value = String(maxVal);
            updateFill();
        };

        const syncFromNumbers = () => {
            let minVal = clamp(parseFloat(minNumber.value || String(boundMin)));
            let maxVal = clamp(parseFloat(maxNumber.value || String(boundMax)));
            if (minVal > maxVal) { const tmp = minVal; minVal = maxVal; maxVal = tmp; }
            minHandle.value = String(minVal);
            maxHandle.value = String(maxVal);
            minNumber.value = String(minVal);
            maxNumber.value = String(maxVal);
            updateFill();
        };

        minHandle.addEventListener('input', () => { syncFromHandles(); this._scheduleCount(); });
        maxHandle.addEventListener('input', () => { syncFromHandles(); this._scheduleCount(); });
        minNumber.addEventListener('change', () => { syncFromNumbers(); this._scheduleCount(); });
        maxNumber.addEventListener('change', () => { syncFromNumbers(); this._scheduleCount(); });

        updateFill();
    },

    // ── Recalcul live du nombre de résultats ────────────────────────────────
    _bindLiveCount() {
        this._change_handler = (event) => {
            const target = event.target;
            if (target.matches('input[type="checkbox"], input[type="radio"]')) {
                this._scheduleCount();
            }
        };
        this._panel.addEventListener('change', this._change_handler);
    },

    _scheduleCount() {
        clearTimeout(this._countTimer);
        this._countTimer = setTimeout(() => this._updateResults(), 200);
    },

    // Recalcule ET rerend la grille + pagination derrière le tiroir (visible à
    // travers le voile semi-transparent), en plus du compteur du bouton —
    // filtrage réellement live, sans attendre le clic sur "Voir les résultats".
    async _updateResults() {
        const form = this._panel?.querySelector('form');
        if (!form || !this._root) return;

        const params = new URLSearchParams(new FormData(form));
        const endpoint = window.location.pathname.replace(/\/$/, '') + '/compter';

        // Deux requêtes peuvent se chevaucher (slider prix + case à cocher
        // déclenchés à quelques ms d'écart) : sans ce garde-fou, une réponse
        // plus ancienne arrivant après la plus récente écraserait le compteur
        // avec un résultat obsolète.
        const sequence = ++this._countSequence;

        try {
            const res = await fetch(`${endpoint}?${params.toString()}`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok || sequence !== this._countSequence) return;
            const data = await res.json();
            if (sequence !== this._countSequence) return;

            const resultsEl = this._root.querySelector('[data-catalogue-results]');
            if (resultsEl && typeof data.html === 'string') resultsEl.innerHTML = data.html;

            const countEl = this._root.querySelector('[data-catalogue-count]');
            if (countEl?.dataset.labelCount) {
                countEl.textContent = countEl.dataset.labelCount.replace('%d', data.total);
            }

            const submitBtn = this._panel.querySelector('[data-catalogue-submit]');
            if (submitBtn?.dataset.labelShowResults) {
                submitBtn.textContent = submitBtn.dataset.labelShowResults.replace('%d', data.total);
                submitBtn.disabled = data.total === 0;
            }
        } catch { /* silencieux */ }
    },

    // Parallax de l'image du hero : elle défile plus lentement que la page
    // (débord de 12% haut/bas géré en CSS). Respecte prefers-reduced-motion.
    _initHeroParallax() {
        const hero  = document.querySelector('.bc-catalogue-hero');
        const media = hero && hero.querySelector('.bc-catalogue-hero__media');
        if (!media) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this._heroTween = gsap.fromTo(media,
            { yPercent: -8 },
            {
                yPercent: 8,
                ease: 'none',
                scrollTrigger: {
                    trigger: hero,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            }
        );
    },

    destroy() {
        if (this._heroTween) {
            if (this._heroTween.scrollTrigger) this._heroTween.scrollTrigger.kill();
            this._heroTween.kill();
            this._heroTween = null;
        }
        gsap.set('.bc-catalogue-hero__media', { clearProps: 'transform' });

        if (this._panel && this._panel.classList.contains('is-open')) {
            document.body.style.overflow = '';
        }
        if (this._button && this._toggle_handler) {
            this._button.removeEventListener('click', this._toggle_handler);
        }
        if (this._panel && this._close_handler) {
            this._panel.querySelectorAll('[data-catalogue-filter-close]').forEach(button => {
                button.removeEventListener('click', this._close_handler);
            });
        }
        if (this._key_handler) document.removeEventListener('keydown', this._key_handler);
        if (this._panel && this._group_handler) {
            this._panel.removeEventListener('click', this._group_handler);
        }
        if (this._panel && this._change_handler) {
            this._panel.removeEventListener('change', this._change_handler);
        }
        clearTimeout(this._countTimer);
        this._root = null;
        this._button = null;
        this._panel = null;
        this._toggle_handler = null;
        this._close_handler = null;
        this._key_handler = null;
        this._group_handler = null;
        this._change_handler = null;
    },
};

export default barcinoCatalogue;
